import { QUIZ_STATES } from '~/features/profile-completion/utils/constants'
import * as Yup from 'yup'
import { remapAnswersWQuestions } from './pc-shared.helper'

/**
 * Retrieves a quiz object from an array of quiz data based on specific state conditions.
 *
 * @param {string|Object[]} jsonData - The quiz data, either as a JSON string or an array of objects.
 * @returns {Object|undefined} The first quiz object that matches the specified conditions, or undefined if no match is found.
 *
 * @description
 * This function searches for a quiz object in the following order of priority:
 * 1. First object with state "answering"
 * 2. First object with state "aborted"
 * 3. First object with state "answered"
 * 4. The first object in the array if none of the above states are found
 *
 * @example
 * const jsonData = '[{"id": "1", "state": "answering"}, {"id": "2", "state": "aborted"}]';
 * const result = getQuizObject(jsonData);
 * console.log(result); // Outputs: {"id": "1", "state": "answering"}
 */
export const getQuizObject = (jsonData) => {
	if (!jsonData) return undefined

	// Parse the JSON data if it's a string
	const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData

	if (data.every((item) => item.state === 'answered')) {
		// Case user submit the last answer of the last required question in the latest quiz
		// If all quizzes are answered, return the last one
		return data[data.length - 1]
	}

	// Find the first object that matches the conditions
	return (
		data.find((item) => item.state === 'answering') ||
		data.find((item) => item.state === 'aborted') ||
		data.find((item) => item.state === 'answered') ||
		data[0]
	) // If no matching state is found, return the first item
}

/**
 * @description
 * This function is used to return the current state of a quiz.
 * if the user answered all questions, the state will be "answered"
 * if the user answered few questions of the form, the state will be "answering"
 * if the user aborted the quiz, the state will be "aborted"
 *
 * @param {boolean} isFormCompleted
 *
 * @return {string} The state of the quiz. value of `QUIZ_STATES`
 * */
export const returnQuizState = (isFormCompleted) => {
	if (isFormCompleted === true) return QUIZ_STATES.ANSWERED
	if (isFormCompleted === false) return QUIZ_STATES.ANSWERING
	return QUIZ_STATES.ABORTED
}

/**
 * Function compare the answers with the total question of the quiz and return if the quiz is completed or not
 *
 * @param {Object} values - Formik's values object
 *
 * @returns {boolean} True if the quiz is completed, false otherwise
 * */
export const areAllFieldsCompleted = (values) => {
	const remapped = remapAnswersWQuestions(values)
	const allQuestions = document.querySelectorAll('[data-opus-question]')

	// Filter out elements that also have data-optional attribute
	const requiredQuestions = Array.from(allQuestions).filter((element) => {
		const optionalElement = element.querySelector('[data-optional]')
		return !optionalElement
	})

	return Object.keys(remapped).length >= requiredQuestions?.length
}

/**
 * Generates a Yup validation schema based on the initial values of the form.
 *
 * @param {Object} initialValues - The initial values object for the Formik form
 * @returns {Yup.ObjectSchema} A Yup schema object for form validation
 */
export const generateValidationSchema = (initialValues) => {
	const schema = {}

	Object.keys(initialValues).forEach((key) => {
		const value = initialValues[key]

		if (typeof value === 'object' && value !== null) {
			// Handle nested objects (e.g., for file uploads)
			schema[key] = Yup.object().shape({
				name: Yup.string().required('This field is required'),
				answer: Yup.mixed().required('This field is required'),
				quizQuestionId: Yup.string().required('Quiz question ID is required'),
			})
		} else {
			// Basic validation for other types
			schema[key] = Yup.object().shape({
				name: Yup.string().required('This field is required'),
				answer: Yup.mixed().required('This field is required'),
				quizQuestionId: Yup.string().required('Quiz question ID is required'),
			})
		}
	})

	return Yup.object().shape(schema)
}
